import type {
  DraftOperation,
  KnowledgeDocument,
  ProposedDraftChange,
} from '../../api';

type PreviewDocument = Pick<KnowledgeDocument, 'path' | 'title' | 'content'>;

export function previewForOperation(
  operation: DraftOperation | undefined,
  documents: KnowledgeDocument[],
) {
  if (!operation) {
    return {
      path: undefined,
      original: '',
      modified: '',
    };
  }

  if (operation.type === 'createFile') {
    return {
      path: operation.path,
      original: '',
      modified: operation.content,
    };
  }

  if (operation.type === 'renameFile') {
    const document = findDocumentForPath(documents, operation.from);

    return {
      path: operation.to,
      original: document?.content ?? '',
      modified: document?.content ?? '',
    };
  }

  if (operation.type === 'deleteFile') {
    const document = findDocumentForPath(documents, operation.path);

    return {
      path: operation.path,
      original: document?.content ?? '',
      modified: '',
    };
  }

  const document = findDocumentForPath(documents, operation.path);

  return {
    path: document?.path ?? operation.path,
    original: document?.content ?? '',
    modified: operation.content || fallbackModifiedContent(document, operation),
  };
}

export function buildPreviewDocument(
  preview: {
    path?: string;
    original: string;
    modified: string;
  },
  documents: KnowledgeDocument[],
): PreviewDocument | undefined {
  if (!preview.path || !supportsRenderedPreview(preview.path)) {
    return undefined;
  }

  const existing = findDocumentForPath(documents, preview.path);

  return {
    path: preview.path,
    title: existing?.title ?? preview.path.split('/').pop() ?? preview.path,
    content: preview.modified,
  };
}

export function buildPendingPreviewDocumentForPath(options: {
  path: string;
  documents: KnowledgeDocument[];
  proposedChanges: ProposedDraftChange[];
}): PreviewDocument | undefined {
  const existing = findDocumentForPath(options.documents, options.path);
  let previewPath = existing?.path ?? options.path;
  let content = existing?.content ?? '';
  let changed = false;

  const operations = pendingOperationsForPath(
    options.proposedChanges,
    options.path,
  );

  for (const operation of operations) {
    changed = true;
    if (operation.type === 'createFile') {
      previewPath = operation.path;
      content = operation.content;
    } else if (operation.type === 'renameFile') {
      previewPath = operation.to;
    } else if (operation.type === 'deleteFile') {
      content = '';
    } else {
      previewPath = operation.path;
      content = operation.content;
    }
  }

  if (!changed || !supportsRenderedPreview(previewPath)) {
    return undefined;
  }

  return {
    path: previewPath,
    title: existing?.title ?? previewPath.split('/').pop() ?? previewPath,
    content,
  };
}

export function pendingOperationsForPath(
  proposedChanges: ProposedDraftChange[],
  path: string,
) {
  return [...proposedChanges]
    .filter(change => change.status === 'pending')
    .sort((left, right) => {
      const leftCreatedAt = new Date(left.createdAt).getTime();
      const rightCreatedAt = new Date(right.createdAt).getTime();
      return leftCreatedAt - rightCreatedAt;
    })
    .flatMap(change =>
      change.operations.filter(
        operation =>
          operation.status === 'pending' &&
          operationTargetsPath(operation, path),
      ),
    );
}

export function operationTargetsPath(operation: DraftOperation, path: string) {
  if (operation.type === 'renameFile') {
    return operation.from === path || operation.to === path;
  }
  return operation.path === path;
}

export function supportsRenderedPreview(path: string) {
  return /\.(md|mdx|png|svg|pdf|docx?|xlsx?|ya?ml|json)$/i.test(path);
}

function findDocumentForPath(
  documents: KnowledgeDocument[],
  path: string,
): KnowledgeDocument | undefined {
  const normalized = normalizePath(path);
  const withoutDocs = normalized.replace(/^docs\//, '');
  const withDocs = normalized.startsWith('docs/')
    ? normalized
    : `docs/${normalized}`;
  const basename = normalized.split('/').pop();

  return (
    documents.find(document => normalizePath(document.path) === normalized) ??
    documents.find(document => normalizePath(document.path) === withoutDocs) ??
    documents.find(document => normalizePath(document.path) === withDocs) ??
    documents.find(document => document.path.split('/').pop() === basename)
  );
}

function normalizePath(path: string) {
  return path
    .trim()
    .replace(/^\/+/, '')
    .replace(/\/+/g, '/');
}

function fallbackModifiedContent(
  document: KnowledgeDocument | undefined,
  operation: DraftOperation,
) {
  if (operation.type !== 'updateFile') {
    return '';
  }

  if (!document) {
    return operation.content;
  }

  return operation.content || document.content;
}
